import { Add, TrashCan } from "@carbon/icons-react";
import { type TableMeta } from "@tanstack/react-table";
import { type FC, useCallback } from "react";

import { Button } from "../../../Button";
import { type ColorVariant } from "../../../Chip/types";
import { type DataGridColumnDefTag } from "../../useColumnState";
import { ColumnHeaderLabel } from "../ColumnHeaderLabel";
import { StandardColumnOptions } from "../StandardColumnOptions";
import { TagColorSelector } from "./TagColorSelector";
import { TagLabel } from "./TagLabel";

export const EditTagOptions: FC<{
	headerID: string;
	columnDef: DataGridColumnDefTag<unknown, unknown>;
	updateColumn: TableMeta<unknown>["updateColumn"];
	updateColumnData: TableMeta<unknown>["updateColumnData"];
}> = ({ headerID, columnDef, updateColumn, updateColumnData }) => {
	const fieldTypeInfo = columnDef.fieldTypeInfo;

	const appendTag = useCallback(() => {
		if (!updateColumn) {
			return;
		}

		const tags = fieldTypeInfo?.tags || [];

		const tagLabels = new Set(tags.map((tag) => tag.label));
		let i = 1;
		let nextLabel = `New option`;
		while (tagLabels.has(nextLabel)) {
			nextLabel = `New option ${i++}`;
		}

		const newFieldInfo: DataGridColumnDefTag<unknown, unknown>["fieldTypeInfo"] = Object.assign(
			{},
			fieldTypeInfo,
			{ tags: [...tags, { label: nextLabel, color: "default" }] }
		);

		const newColumnDef: DataGridColumnDefTag<unknown, unknown> = Object.assign({}, columnDef, {
			fieldTypeInfo: newFieldInfo,
		});

		updateColumn(headerID, newColumnDef);
	}, [fieldTypeInfo, headerID, columnDef, updateColumn]);

	const updateTagColor = useCallback(
		(tagIndex: number, color: ColorVariant) => {
			const tag = fieldTypeInfo?.tags[tagIndex];
			if (!tag || !updateColumn) {
				return;
			}
			const newTag = { ...tag, color };
			const newFieldInfo: DataGridColumnDefTag<unknown, unknown>["fieldTypeInfo"] =
				Object.assign({}, fieldTypeInfo, {
					tags: [
						...fieldTypeInfo.tags.slice(0, tagIndex),
						newTag,
						...fieldTypeInfo.tags.slice(tagIndex + 1),
					],
				});

			const newColumnDef: DataGridColumnDefTag<unknown, unknown> = Object.assign(
				{},
				columnDef,
				{
					fieldTypeInfo: newFieldInfo,
				}
			);

			updateColumn?.(headerID, newColumnDef);
		},
		[fieldTypeInfo, headerID, columnDef, updateColumn]
	);

	const updateTagLabel = useCallback(
		(tagIndex: number, label: string) => {
			const tag = fieldTypeInfo?.tags[tagIndex];
			if (!tag || !updateColumn) {
				return;
			}

			const tagLabels = new Set(fieldTypeInfo.tags.map((tag) => tag.label));
			if (tagLabels.has(label)) {
				return;
			}

			const newTag = { ...tag, label };
			const newFieldInfo: DataGridColumnDefTag<unknown, unknown>["fieldTypeInfo"] =
				Object.assign({}, fieldTypeInfo, {
					tags: [
						...fieldTypeInfo.tags.slice(0, tagIndex),
						newTag,
						...fieldTypeInfo.tags.slice(tagIndex + 1),
					],
				});

			const newColumnDef: DataGridColumnDefTag<unknown, unknown> = Object.assign(
				{},
				columnDef,
				{
					fieldTypeInfo: newFieldInfo,
				}
			);

			updateColumn?.(headerID, newColumnDef);

			updateColumnData?.(headerID, (value) => {
				if (Array.isArray(value)) {
					return value.map((v) => (v === tag.label ? newTag.label : v));
				}

				if (value !== tag.label) {
					return value;
				}

				return newTag.label;
			});
		},
		[fieldTypeInfo, headerID, columnDef, updateColumn]
	);

	const deleteTag = useCallback(
		(tagIndex: number) => {
			const tag = fieldTypeInfo?.tags[tagIndex];
			if (!tag || !fieldTypeInfo || !updateColumn) {
				return;
			}

			const newFieldInfo: DataGridColumnDefTag<unknown, unknown>["fieldTypeInfo"] =
				Object.assign({}, fieldTypeInfo, {
					tags: [
						...fieldTypeInfo.tags.slice(0, tagIndex),
						...fieldTypeInfo.tags.slice(tagIndex + 1),
					],
				});

			const newColumnDef: DataGridColumnDefTag<unknown, unknown> = Object.assign(
				{},
				columnDef,
				{
					fieldTypeInfo: newFieldInfo,
				}
			);

			updateColumn?.(headerID, newColumnDef);

			updateColumnData?.(headerID, (value) => {
				if (Array.isArray(value)) {
					return value.filter((v) => v !== tag.label);
				}

				if (value !== tag.label) {
					return value;
				}

				return undefined;
			});
		},
		[fieldTypeInfo, headerID, columnDef, updateColumn]
	);

	return (
		<div className="flex flex-col space-y-4">
			<div className="flex flex-col space-y-2">
				<div className="mt-2">
					<ColumnHeaderLabel htmlFor="column-readonly-checkbox">
						Options
					</ColumnHeaderLabel>
				</div>
				<div className="flex flex-col space-y-1">
					{fieldTypeInfo?.tags.map((tag, tagIndex) => (
						<div key={`${tag.label}-${tag.color}`} className="flex h-8 items-center">
							<Button className="h-8 w-8" level="tertiary" size="md">
								<TagColorSelector
									selectedColor={tag.color}
									onColorChange={(color) => updateTagColor(tagIndex, color)}
								/>
							</Button>
							<div className="flex grow flex-col">
								<TagLabel
									tagLabel={tag.label}
									onTagLabelChange={(tagLabel) =>
										updateTagLabel(tagIndex, tagLabel)
									}
								/>
							</div>
							<div className="ml-auto">
								<Button
									onClick={() => deleteTag(tagIndex)}
									size="sm"
									variant="negative"
									level="tertiary"
									icon={TrashCan}
									label="Remove"
								/>
							</div>
						</div>
					))}
				</div>
				<div className="-ml-0.5">
					<Button
						size="sm"
						level="tertiary"
						icon={Add}
						label="Add Option"
						onClick={appendTag}
					>
						Add Option
					</Button>
				</div>

				<StandardColumnOptions
					headerID={headerID}
					columnDef={columnDef}
					updateColumn={updateColumn}
				/>
			</div>
		</div>
	);
};
